import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MobXProviderContext } from 'mobx-react';
import { queryKeys } from '@portal/ui/hooks/queryKeys';
import { NO_TEAM_DISCOUNTS } from '@bw-ui/basket';
import type { TeamDiscounts } from '@bw-ui/basket';

interface Team {
  DiscountExtraServices?: number;
  DiscountCharges?: number;
  DiscountTariffs?: number;
  DiscountTimePasses?: number;
}

function maxField(teams: Team[], field: keyof Team): number {
  return Math.max(...teams.map((t) => t[field] ?? 0));
}

/**
 * Fetch the user's team discount percentages from the Nexudus teams API.
 * Returns per-type discount percentages (bookings, tariffs, timePasses, charges),
 * each being the highest value across all teams the user belongs to.
 *
 * IMPORTANT: Components using this hook must be wrapped with MobX `observer()`.
 */
export function useTeamDiscount() {
  const stores = useContext(MobXProviderContext) as Record<string, unknown>;

  if (!stores?.authStore) {
    throw new Error('useTeamDiscount must be used within a MobX Provider');
  }

  const store = stores.authStore as {
    agent?: { Auth?: { getTeams?: () => Promise<Team[]> } };
  };

  const canFetch = typeof store.agent?.Auth?.getTeams === 'function';

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.teamDiscount,
    queryFn: async () => {
      const teams: Team[] = await store.agent!.Auth!.getTeams!();
      if (!Array.isArray(teams) || teams.length === 0) return NO_TEAM_DISCOUNTS;
      return {
        bookings: maxField(teams, 'DiscountExtraServices'),
        tariffs: maxField(teams, 'DiscountTariffs'),
        timePasses: maxField(teams, 'DiscountTimePasses'),
        charges: maxField(teams, 'DiscountCharges'),
      } satisfies TeamDiscounts;
    },
    enabled: canFetch,
    staleTime: 5 * 60 * 1000,
  });

  return {
    teamDiscounts: data ?? NO_TEAM_DISCOUNTS,
    isLoading,
  };
}
